import store from "~/store";
import { DatabaseClient, getDatabaseClient } from '~/data/db/DatabaseClient';
import { resolveOfflineDb } from './offlineDatabase';
import { isLastUsedMachine } from '~/sqlite/lastUsedCache';
import QueryBuilder from "./queryBuilder";
import {
    IssueCategory,
    IssueDetails,
    IssueDetailsTable,
    IssuePayload,
    IssueResponsePayload,
    IssueSortingType,
    Label
} from "~/types";
import FileTransfer from "~/sqlite/services/fileTransfer";
import moment from 'moment';
import _ from 'lodash';
import {syncTypes} from "~/sqlite/syncDataTypes";
import { syncQueue } from '~/data/sync/SyncQueue';
import { notifyPeriodicCheckQueueChanged } from '~/data/sync/periodicCheckQueue';
import { IssueStatuses, IssueTypeRepaired } from "~/app/data/statuses/issue";
import { diffByRowDataEquality } from '~/data/local/diffHelper';

export const cleanLocalStoredPath = (pathOrUrl: string): string => {
    if (!pathOrUrl || typeof pathOrUrl !== 'string') return '';
    if (/^https?:\/\/(?!localhost|127\.0\.0\.1)/i.test(pathOrUrl)) {
        return pathOrUrl;
    }
    let cleaned = pathOrUrl;
    cleaned = cleaned
        .replace(/^ionic:\/\/localhost\/_app_file_\/?/i, '')
        .replace(/^https?:\/\/localhost(:\d+)?\/_app_file_\/?/i, '')
        .replace(/^https?:\/\/localhost:\d+\/?/i, '')
        .replace(/^file:\/\/\/?/i, '')
        .replace(/^cdvfile:\/\/localhost\/[^/]+\/?/i, '');

    const dataDir = window['cordova']?.file?.dataDirectory ?? '';
    if (dataDir) {
        const cleanedDataDir = dataDir.replace(/^file:\/\/\/?/i, '').replace(/^\//, '').replace(/\/$/, '');
        if (cleanedDataDir && cleaned.includes(cleanedDataDir)) {
            cleaned = cleaned.replace(cleanedDataDir, '');
        }
    }
    const tempIndex = cleaned.lastIndexOf('temporary/');
    if (tempIndex !== -1) {
        cleaned = cleaned.substring(tempIndex);
    }
    return cleaned.replace(/^\/+/, '');
};

export class IssueOfflineService {

    constructor(private readonly databaseClient: DatabaseClient | null = getDatabaseClient()) {}

    private readonly qb: QueryBuilder = new QueryBuilder();
    private get SQLite() {
        return resolveOfflineDb(this.databaseClient);
    }

    public resetIssueCategories(data: IssueCategory[]): Promise<void> {
        const db = this.SQLite;
        if (!db) return Promise.resolve();
        return new Promise<void>((resolve, reject) => {
            db.transaction(
                (tx) => {
                    const { query: truncateQuery, params: truncateParams } = this.qb.truncate('IssueCategories');
                    tx.executeSql(truncateQuery, truncateParams);
                    const { query: insertQuery, params: insertParams } = this.qb.insert('IssueCategories', data);
                    tx.executeSql(insertQuery, insertParams);
                },
                (e) => reject(e),
                resolve
            );
        });
    }

    public getAllIssueCategories(): Promise<IssueCategory[]> {
        const db = this.SQLite;
        if (!db) return Promise.resolve([]);
        return new Promise<IssueCategory[]>((resolve, reject) => {
            const { query, params } = this.qb.select('IssueCategories');
            db.executeSql(query, params, (resultSet) => resolve(this.qb.getAllResult(resultSet)), (e) => reject(e));
        });
    }

    public resetIssueLabels(data: Label[]): Promise<void> {
        const db = this.SQLite;
        if (!db) return Promise.resolve();
        return new Promise<void>((resolve, reject) => {
            db.transaction(
                (tx) => {
                    const { query: truncateQuery, params: truncateParams } = this.qb.truncate('IssueLabels');
                    tx.executeSql(truncateQuery, truncateParams);
                    const { query: insertQuery, params: insertParams } = this.qb.insert('IssueLabels', data);
                    tx.executeSql(insertQuery, insertParams);
                },
                (e) => reject(e),
                resolve
            );
        });
    }

    public getAllIssueLabels(): Promise<Label[]> {
        const db = this.SQLite;
        if (!db) return Promise.resolve([]);
        return new Promise<Label[]>((resolve, reject) => {
            const { query, params } = this.qb.select('IssueLabels');
            db.executeSql(query, params, (resultSet) => resolve(this.qb.getAllResult(resultSet)), (e) => reject(e));
        });
    }

    public resetIssueSortingTypes(data: IssueSortingType[]): Promise<void> {
        const db = this.SQLite;
        if (!db) return Promise.resolve();
        return new Promise<void>((resolve, reject) => {
            db.transaction(
                (tx) => {
                    const { query: truncateQuery, params: truncateParams } = this.qb.truncate('IssueSortingTypes');
                    tx.executeSql(truncateQuery, truncateParams);
                    const { query: insertQuery, params: insertParams } = this.qb.insert('IssueSortingTypes', data);
                    tx.executeSql(insertQuery, insertParams);
                },
                (e) => reject(e),
                resolve
            );
        });
    }

    public getAllIssueSortingTypes(): Promise<IssueSortingType[]> {
        const db = this.SQLite;
        if (!db) return Promise.resolve([]);
        return new Promise<IssueSortingType[]>((resolve, reject) => {
            const { query, params } = this.qb.select('IssueSortingTypes');
            db.executeSql(query, params, (resultSet) => resolve(this.qb.getAllResult(resultSet)), (e) => reject(e));
        });
    }

    public reset(issues: IssueDetails[]): Promise<void> {
        const db = this.SQLite;
        if (!db) return Promise.resolve();

        return new Promise<void>((resolve, reject) => {
            this.checkIssues(issues).then(({issues, deleteIssues}) => {
                db.transaction((tx) => {
                    if(deleteIssues.length){
                        const { query, params } = this.qb.deleteIn('Issue' , 'id',  deleteIssues);
                        tx.executeSql(query, params);
                    }

                    if(issues) {
                        const { query, params } = this.qb.insertOrReplace('Issue', issues);
                        tx.executeSql(query, params);
                    }
                }, (e) => reject(e), resolve);
            });
        });
    }

    public checkIssues(issuesWithImages: IssueDetails[]): Promise<{issues: IssueDetailsTable[], deleteIssues: string[]}> {
        const db = this.SQLite;
        if (!db) return Promise.resolve({ issues: [], deleteIssues: [] });

        const { query, params } = this.qb.select('Issue');

        return new Promise<{issues: IssueDetailsTable[], deleteIssues: string[]}>((resolve, reject) => {
            db.executeSql(query, params, (resultSet) => {
                const issues = issuesWithImages.map((data) => ({
                    data: JSON.stringify({ ...data, images: '' }),
                    id: data.id,
                    machineId: data.machineId,
                    images: '',
                }));

                const dbIssues = this.qb.getAllResult(resultSet);
                const { toAdd, toDeleteIds } = diffByRowDataEquality(dbIssues, issues);

                resolve({ issues: toAdd, deleteIssues: toDeleteIds });

            }, (e) => reject(e));
        })
    }

    public updateIssueDetail(issue: IssueDetails) {
        const db = this.SQLite;
        if (db && (store.getState().machines.inFavorite(issue.machineId) || isLastUsedMachine(issue.machineId))) {
            const qb = new QueryBuilder();
            this.downloadIssueImages(issue).then((issue) => {
                db.transaction((tx) => {
                    const { query, params } = qb.update('Issue',
                        {
                            id: issue.id,
                            machineId: issue.machineId,
                            images: JSON.stringify(issue.images),
                            data: JSON.stringify({...issue, images: ''})},
                        [{name: 'id', value: issue.id}]
                    );
                    tx.executeSql(query, params);
                });
            });
        }
    }

    public downloadIssuesImages(issues: IssueDetails[]): Promise<IssueDetails[]> {
        const allPromise: Promise<IssueDetails>[] = [];
        issues.forEach((issues) => {
            allPromise.push(this.downloadIssueImages(issues));
        });

        return Promise.all(allPromise).then(machines => {
            return machines;
        });
    }

    public downloadIssueImages(issue: IssueDetails): Promise<IssueDetails> {
        return new Promise<IssueDetails>((resolve) => {
            if (issue.images) {
                const fileTransfer = new FileTransfer();
                const allPromise: Promise<string>[] = [];
                issue.images.forEach((image) => {
                    let imageDownloadPromise = new Promise<string>((resolve) => {
                        fileTransfer.downloadFileFromURL(image, `issueImages/${issue.machineId}/${issue.id}`).then((image) => {
                            resolve(image);
                        });
                    });
                    allPromise.push(imageDownloadPromise);
                });
                Promise.all(allPromise).then(images => resolve({...issue, ...{images: images.filter(item => item)}}));
            } else {
                resolve(issue);
            }
        });
    }

    public getIssue(id: string): Promise<IssueDetails | undefined> {
        const db = this.SQLite;
        if (!db) return Promise.resolve(undefined);

        const where = [{name: 'id', value: id}];
        const { query, params } = this.qb.select('Issue', [], where);

        return new Promise<IssueDetails>((resolve, reject) => {
            db.executeSql(query, params, (result) => {
                const issue = this.qb.getSingleResult(result);
                if (!issue) {
                    reject(new Error('Issue not found'));
                    return;
                }

                const issueData = JSON.parse(issue.data);
                let rawImagesList: string[] = [];
                let hasImagesFieldInRow = false;
                if (issue.images !== undefined && issue.images !== null) {
                    hasImagesFieldInRow = true;
                    try {
                        const parsed = JSON.parse(issue.images);
                        if (Array.isArray(parsed)) {
                            rawImagesList = parsed;
                        }
                    } catch (e) {
                        console.error('Failed to parse issue.images', e);
                    }
                }
                if (!hasImagesFieldInRow && issueData.images && Array.isArray(issueData.images)) {
                    rawImagesList = issueData.images;
                }

                const images = rawImagesList.map((path: string) => {
                    if (!_.isString(path) || !path) return path;
                    if (path.startsWith('http') || path.startsWith('blob:')) return path;

                    const dataDir = window['cordova']?.file?.dataDirectory ?? '';
                    let fullPath = path;
                    if (dataDir) {
                        const cleanedDataDir = dataDir.endsWith('/') ? dataDir : `${dataDir}/`;
                        const cleanedPath = path.startsWith(cleanedDataDir)
                            ? path.replace(cleanedDataDir, '')
                            : path.replace(/^\//, '');
                        fullPath = `${cleanedDataDir}${cleanedPath}`;
                    }
                    if (window['WkWebView']?.convertFilePath && fullPath.startsWith('file:')) {
                        return window['WkWebView'].convertFilePath(fullPath);
                    }
                    if (window['Ionic']?.WebView?.convertFileSrc && fullPath.startsWith('file:')) {
                        return window['Ionic'].WebView.convertFileSrc(fullPath);
                    }
                    return fullPath;
                });

                let files = [];
                if (issueData.files && Array.isArray(issueData.files)) {
                    files = issueData.files.map((path: string) =>
                        _.isString(path) && !path.startsWith('http')
                            ? `${window['cordova']?.file?.dataDirectory ?? ''}/${path.replace(`${window['cordova']?.file?.dataDirectory ?? ''}/`, '').replace(/^\//, '')}`
                            : path
                    );
                }

                let categoryId = issueData.categoryId ? Number(issueData.categoryId) : undefined;
                let categoryName = issueData.categoryName || undefined;
                if (!categoryId && categoryName) {
                    const storeCategories = store.getState().issue.categories || [];
                    const match = storeCategories.find(c => c.name?.trim().toLowerCase() === categoryName.trim().toLowerCase());
                    if (match) {
                        categoryId = match.id;
                    }
                }

                let issueDepartmentId = issueData.issueDepartmentId ? Number(issueData.issueDepartmentId) : (issueData.departmentId ? Number(issueData.departmentId) : undefined);
                let issueDepartmentName = issueData.issueDepartmentName || issueData.departmentName || undefined;
                if (!issueDepartmentId && issueDepartmentName) {
                    const storeDepartments = store.getState().app.appData.departments || [];
                    const match = storeDepartments.find(d => d.name?.trim().toLowerCase() === issueDepartmentName.trim().toLowerCase());
                    if (match) {
                        issueDepartmentId = match.id;
                    }
                }

                resolve({
                    ...issueData,
                    id: issue.id,
                    machineId: issueData.machineId || String(issue.machineId || ''),
                    categoryId,
                    categoryName,
                    issueDepartmentId,
                    issueDepartmentName,
                    departmentId: issueDepartmentId,
                    images,
                    files
                })
            }, (e) => reject(e));
        });
    }

    public saveIssue(data: IssuePayload): Promise<IssueResponsePayload | { id: string; type: number }> {
        const db = this.SQLite;
        if (!db) {
            return Promise.resolve({id: '', type: parseInt(data.type)});
        }

        const fileTransfer = new FileTransfer();
        const departments = store.getState().app.appData.departments;
        const users = store.getState().app.appData.users;
        const issueDepartmentIndex = departments.findIndex(department => Number(department.id) === Number(data.departmentId));
        let issueId = '';
        const updatedIssueId = data?.id ?? null;
        const issueDetails = store.getState().issue.data.id ? store.getState().issue.data : null;
        const userIndex = users.findIndex(user => data.performedBy && user.id === parseInt(data.performedBy));
        let issuePayloadId = '';
        let existingImagesData: string[] = [];
        let newImagesArray: File[] = [];
        const rawImages = data?.images;
        if (Array.isArray(rawImages)) {
            rawImages.forEach((image: string | File) => {
                if (_.isString(image)) {
                    const cleaned = cleanLocalStoredPath(image);
                    if (cleaned) {
                        existingImagesData.push(cleaned);
                    }
                } else if (image) {
                    newImagesArray.push(image);
                }
            });
        }

        let existingFilesData: string[] = [];
        let newFilesArray: File[] = [];
        const rawFiles = data?.files;
        if (Array.isArray(rawFiles)) {
            rawFiles.forEach((file: string | File) => {
                if (_.isString(file)) {
                    const cleaned = cleanLocalStoredPath(file);
                    if (cleaned) {
                        existingFilesData.push(cleaned);
                    }
                } else if (file) {
                    newFilesArray.push(file);
                }
            });
        }

        return fileTransfer.saveFilesFromBlobs(newImagesArray).then((images) => {
            return fileTransfer.saveFilesFromBlobs(newFilesArray).then((savedFiles) => {
                console.log('save in offlineMode', data);
                return new Promise<IssueResponsePayload>((resolve, reject) => {
                    let imagesJson = '';
                    let dataJson = '';
                    db.transaction((tx) => {
                        const imagesCleared = !rawImages || (Array.isArray(rawImages) && rawImages.length === 0) || rawImages === ('' as unknown as typeof rawImages);
                        const imagesData = imagesCleared ? [] : existingImagesData.concat(images);
                        imagesJson = JSON.stringify(imagesData ?? []);

                        // Document paths live in data JSON (no schema column) for offline parity without DB migration
                        const filesCleared = !rawFiles || (Array.isArray(rawFiles) && rawFiles.length === 0) || rawFiles === ('' as unknown as typeof rawFiles);
                        const filesData = filesCleared
                            ? ''
                            : existingFilesData.concat(savedFiles);

                        const issueRecord = {
                            status: data.issueType === 'repair' ? IssueStatuses.COMPLETED : IssueStatuses.REGISTERED,
                            ...data,
                            departmentId: data.departmentId ? Number(data.departmentId) : null,
                            departmentName: issueDepartmentIndex !== -1 ? departments[issueDepartmentIndex].name : (issueDetails?.departmentName ?? null),
                            categoryId: data.categoryId ? Number(data.categoryId) : (issueDetails?.categoryId ? Number(issueDetails.categoryId) : null),
                            images: undefined,
                            files: filesData,
                            chat: issueDetails?.chat ?? [], 
                            chatUsers: issueDetails?.chatUsers ?? [],
                            workDetails: issueDetails?.workDetails ?? [],
                            type: data.issueType === 'repair' ? IssueTypeRepaired : Number(data.type),
                            statusDate: issueDetails?.statusDate ?? moment().format("YYYY-MM-DD HH:mm:ss"),
                            machineIntern: store.getState().machine.data.intern,
                            machineName: store.getState().machine.data.name,
                            operatingTime: store.getState().machine.data.operatingTime,
                            operatingCount: store.getState().machine.data.operatingCount,
                            issueDepartmentName: issueDepartmentIndex !== -1 ? departments[issueDepartmentIndex].name : (issueDetails?.issueDepartmentName ?? ''),
                            issueDepartmentId: data.departmentId ? Number(data.departmentId) : (issueDetails?.issueDepartmentId ? Number(issueDetails.issueDepartmentId) : null),
                            createdAt: issueDetails?.createdAt ?? moment().format("YYYY-MM-DD HH:mm:ss"),
                            createdBy: issueDetails?.createdBy ?? (userIndex !== -1 ? users[userIndex].name : store.getState().user.data.name),
                            issueCreatedBy: issueDetails?.issueCreatedBy ?? (userIndex !== -1 ? users[userIndex].name : store.getState().user.data.name),
                            issueCreatedUserId: issueDetails?.issueCreatedUserId ?? store.getState().user.data.id,
                            registrationDate: issueDetails?.registrationDate ?? moment().format("YYYY-MM-DD HH:mm:ss"),
                            ...(updatedIssueId && {
                                oldDepartmentId: issueDetails?.issueDepartmentId ? Number(issueDetails?.issueDepartmentId) : null,
                                updatedAt: moment().format("YYYY-MM-DD HH:mm:ss"),
                                issueUpdatedBy: store.getState().user.data.name,
                            }),
                        };
                        const { id: _ignoredId, ...syncQueuePayload } = issueRecord;
                        dataJson = JSON.stringify(data.isCreatedFromOfflineMode ? syncQueuePayload : issueRecord);
                        const storedDataJson = JSON.stringify({ ...issueRecord, pendingSync: true });

                        if(updatedIssueId) {
                            const { query, params } = this.qb.update('Issue', {
                                images: imagesJson,
                                data: storedDataJson,
                                machineId: data.machineId || issueDetails?.machineId || ''
                            }, [{name: 'id', value: updatedIssueId}]);
                            issueId = String(updatedIssueId);
                            tx.executeSql(query, params);
                        } else {
                            const { query, params } = this.qb.insert('Issue', [{
                                data: storedDataJson,
                                images: imagesJson,
                                machineId: data.machineId || issueDetails?.machineId || ''
                            }]);
                            tx.executeSql(query, params, (_SQLite, result) => (issueId = String(result.insertId ?? '')));
                        }

                    }, (e) => reject(e), () => {
                        syncQueue.enqueueReplace(
                            [
                                {
                                    data: dataJson,
                                    images: imagesJson,
                                    userId: Number(store.getState().user.data.id),
                                    typeOfData: syncTypes.ADD_ISSUE,
                                },
                            ],
                            (existing, next) => {
                                if (next.id !== undefined && next.id !== null && String(existing.id) === String(next.id)) {
                                    return true;
                                }
                                if (next.guid && existing.guid && String(existing.guid) === String(next.guid)) {
                                    return true;
                                }
                                return false;
                            }
                        ).then((queueId) => {
                            notifyPeriodicCheckQueueChanged();
                            issuePayloadId = String(queueId ?? '');
                            resolve({id: issueId, type: parseInt(data.type), offlineIssueId: issuePayloadId});
                        }, (e) => reject(e));
                    });
                });
            });
        });
    }

    public removeIssueById(id: string | number): Promise<void> {
        const db = this.SQLite;
        if (!db) return Promise.resolve();
        const { query, params } = this.qb.delete('Issue', [{ name: 'id', value: String(id) }]);
        return new Promise<void>((resolve, reject) => {
            db.executeSql(query, params, () => resolve(), (e) => reject(e));
        });
    }

    public removeIssueByGuid(guid: string): Promise<void> {
        const db = this.SQLite;
        if (!db || !guid) return Promise.resolve();
        return new Promise<void>((resolve, reject) => {
            const { query, params } = this.qb.select('Issue');
            db.executeSql(query, params, (resultSet) => {
                const rows = this.qb.getAllResult(resultSet) || [];
                const matchingRow = rows.find(r => {
                    try {
                        const parsed = JSON.parse(r.data || '{}');
                        return parsed.guid === guid;
                    } catch {
                        return false;
                    }
                });
                if (matchingRow?.id) {
                    const { query: delQuery, params: delParams } = this.qb.delete('Issue', [{ name: 'id', value: String(matchingRow.id) }]);
                    db.executeSql(delQuery, delParams, () => resolve(), (e) => reject(e));
                } else {
                    resolve();
                }
            }, (e) => reject(e));
        });
    }
}
